import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Bot, TrendingUp, MessageSquare, Zap, Shield, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Logo from '../components/Logo';

const LandingPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 relative overflow-hidden">
        <Header transparent />
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.1\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"2\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"}}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <Logo size="xl" />
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-display">
              {t('heroTitle')}
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-100 mb-8 max-w-3xl mx-auto">
              {t('heroSubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/register" 
                className="bg-white text-primary-600 hover:bg-primary-50 font-bold py-4 px-8 rounded-lg transition-colors duration-200 flex items-center space-x-2 shadow-lg"
              >
                <span>{t('startFreeTrial')}</span>
                <ArrowRight size={20} />
              </Link>
              <Link 
                to="/login" 
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-4 px-8 rounded-lg transition-colors duration-200"
              >
                {t('alreadyHaveAccount')}
              </Link>
            </div>
            
            <p className="text-primary-200 text-sm mt-4">
              {t('freeTrialBenefits')}
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4 font-display">
              {t('howItTransforms')}
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Uma plataforma completa que combina atendimento inteligente, vendas automatizadas e marketing digital em uma única solução.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="card-hover text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bot className="text-primary-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">{t('personalizedAssistant')}</h3>
              <p className="text-secondary-600">
                Criamos um assistente único para seu negócio que conhece seus produtos, seu tom de voz e atende seus clientes como você faria.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="card-hover text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="text-primary-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">{t('customerService247')}</h3>
              <p className="text-secondary-600">
                WhatsApp, Instagram, Facebook - seus clientes são atendidos instantaneamente em todos os canais, a qualquer hora do dia.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="card-hover text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-primary-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">{t('trafficManagement')}</h3>
              <p className="text-secondary-600">
                Nossa IA cria, otimiza e gerencia suas campanhas do Google e Meta, maximizando automaticamente seu ROI.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4 font-display">
              {t('howItWorks')}
            </h2>
            <p className="text-xl text-secondary-600">
              {t('threeSteps')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="bg-primary-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">{t('registerBusiness')}</h3>
              <p className="text-secondary-600">
                {t('registerBusinessDesc')}
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="bg-primary-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">{t('connectChannels')}</h3>
              <p className="text-secondary-600">
                {t('connectChannelsDesc')}
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="bg-primary-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-3">{t('setBudget')}</h3>
              <p className="text-secondary-600">
                {t('setBudgetDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6 font-display">
                {t('productsAndServices')}
              </h2>
              <p className="text-xl text-secondary-600 mb-8">
                {t('businessModel')}
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Zap className="text-green-600" size={16} />
                  </div>
                  <div>
                    <h3 className="font-bold text-secondary-900 mb-1">{t('productSales')}</h3>
                    <p className="text-secondary-600">
                      {t('productSalesDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Shield className="text-blue-600" size={16} />
                  </div>
                  <div>
                    <h3 className="font-bold text-secondary-900 mb-1">{t('leadQualification')}</h3>
                    <p className="text-secondary-600">
                      {t('leadQualificationDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-purple-100 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Clock className="text-purple-600" size={16} />
                  </div>
                  <div>
                    <h3 className="font-bold text-secondary-900 mb-1">{t('timeSavings')}</h3>
                    <p className="text-secondary-600">
                      {t('timeSavingsDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">{t('realResults')}</h3>
              
              <div className="space-y-6">
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-3xl font-bold mb-1">+300%</div>
                  <div className="text-primary-100">{t('increaseConversions')}</div>
                </div>
                
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-3xl font-bold mb-1">24/7</div>
                  <div className="text-primary-100">{t('nonStopService')}</div>
                </div>
                
                <div className="bg-white bg-opacity-20 rounded-lg p-4">
                  <div className="text-3xl font-bold mb-1">-80%</div>
                  <div className="text-primary-100">{t('reductionResponseTime')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-display">
            {t('readyToAutomate')}
          </h2>
          <p className="text-xl text-secondary-300 mb-8">
            {t('joinCompanies')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/register" 
              className="bg-primary-500 hover:bg-primary-600 text-white font-bold py-4 px-8 rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <span>{t('startFreeTrial')}</span>
              <ArrowRight size={20} />
            </Link>
            <p className="text-secondary-400 text-sm">
              {t('noCommitment')} • {t('cancelAnytime')}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;

